import React, { useState, useEffect } from 'react';
import Tooltip from '@/components/Tooltip';
import { PriceText } from '@/components/PriceText';
import { EmptyDash, smallNum, humanizeNum } from '../../utils';
import type { Influencer, KpiOption } from '../../types';

/** KPI 한 행: icon or label + 값 + tooltip */
export function KPIRow({ icon, label, shortLabel, tooltip, value }: { icon?: string; label?: React.ReactNode; shortLabel?: string; tooltip: string; value: React.ReactNode }) {
  const labelInner = icon ? (
    <span className="kpi-row-label">
      <i className={`fa fa-${icon} kpi-row-icon`} />
      {shortLabel && <span className="kpi-row-label-text">{shortLabel}</span>}
    </span>
  ) : (
    <span className="kpi-row-label">{label}</span>
  );
  return (
    <Tooltip content={tooltip}>
      <div className="kpi-row">
        {labelInner}
        <span className="kpi-row-value">{value}</span>
      </div>
    </Tooltip>
  );
}

export function KPIDisplay({ kpi }: { kpi: Record<string, unknown> }) {
  if (!kpi || Object.keys(kpi).length === 0) return null;
  const kpiType = (kpi.type as string) ?? 'youtube';
  const pricePerClick = (kpi.pricePerClickWithCom ?? kpi.pricePerClick) as number | undefined;
  const pricePerView = (kpi.pricePerViewWithCom ?? kpi.pricePerView) as number | undefined;
  const pricePerViewer = kpi.pricePerViewer as number | undefined;
  const avgClicks = kpi.avgClicks as number | undefined;
  const avgViews = kpi.avgViews as number | undefined;
  const avgViewers = kpi.avgViewers as number | undefined;
  const maximumViewers = kpi.maximumViewers as number | undefined;
  const postsNumber = kpi.postsNumber as number | undefined;
  const campaignNumber = kpi.campaignNumber as number | undefined;

  return (
    <div className="kpi-display">
      {pricePerClick != null && (
        <KPIRow icon="hand-pointer-o" shortLabel="$/click" tooltip="Price per click" value={<PriceText value={`$${smallNum(pricePerClick)}`} />} />
      )}
      {pricePerView != null && (
        <KPIRow icon="eye" shortLabel="$/view" tooltip="Price per view" value={<PriceText value={`$${smallNum(pricePerView)}`} />} />
      )}
      {pricePerViewer != null && (
        <KPIRow icon="user-circle-o" shortLabel="$/viewer" tooltip="Price per viewer" value={<PriceText value={`$${smallNum(pricePerViewer)}`} />} />
      )}
      {avgClicks != null && (
        <KPIRow
          label={<>Avg. <i className="fa fa-hand-pointer-o kpi-avg-icon" /></>}
          tooltip="Average clicks per video/stream"
          value={humanizeNum(avgClicks)}
        />
      )}
      {avgViews != null && (
        <KPIRow
          label={<>Avg. <i className="fa fa-eye kpi-avg-icon" /></>}
          tooltip="Average views per video/stream"
          value={humanizeNum(avgViews)}
        />
      )}
      {maximumViewers != null && kpiType === 'twitch' && (
        <KPIRow
          label={<>Max. <i className="fa fa-user-circle-o kpi-avg-icon" /></>}
          tooltip="Maximum viewers"
          value={humanizeNum(maximumViewers)}
        />
      )}
      {avgViewers != null && (
        <KPIRow
          label={<>Avg. <i className="fa fa-user-circle-o kpi-avg-icon" /></>}
          tooltip="Average viewers per stream"
          value={humanizeNum(avgViewers)}
        />
      )}
      {postsNumber != null && (
        <KPIRow icon="video-camera" shortLabel={kpiType === 'twitch' ? 'Streams' : 'Posts'} tooltip={kpiType === 'twitch' ? 'Number of streams analyzed' : 'Number of videos/streams analyzed'} value={humanizeNum(postsNumber)} />
      )}
      {campaignNumber != null && (
        <KPIRow icon="briefcase" shortLabel="Camps." tooltip="Number of campaigns analyzed" value={humanizeNum(campaignNumber)} />
      )}
    </div>
  );
}

/** DetailedKPI 셀 (원본 DetailedKPI + KPI) - KPI 옵션에 따른 상세 KPI 표시 */
export function DetailedKPICell({ user, forceKpi, campaignKpi, showEmptyDash = true }: { user: Influencer; forceKpi: KpiOption; campaignKpi?: Record<string, unknown>; showEmptyDash?: boolean }) {
  const orgEntry = user.kpiByOrganization?.[0];
  const kpi = orgEntry?.kpi as Record<string, unknown> | undefined;
  const recentKpi = orgEntry?.recentKpi as Record<string, unknown> | undefined;
  const kpiByFilteredCampaigns = user.kpiByFilteredCampaigns as Record<string, unknown> | undefined;

  const [type, setType] = useState<KpiOption>(() => (forceKpi === 'freeChoice' ? 'allTime' : forceKpi));
  useEffect(() => {
    setType(forceKpi === 'freeChoice' ? 'allTime' : forceKpi);
  }, [forceKpi]);

  const isEmpty = (o: Record<string, unknown> | undefined) => !o || Object.keys(o).length === 0;
  const uniqValid = [kpi, recentKpi, campaignKpi, kpiByFilteredCampaigns].filter((o) => !isEmpty(o));
  const uniqKpis = Array.from(new Map(uniqValid.map((o) => [JSON.stringify(o), o])).values());

  if (forceKpi === 'freeChoice' && uniqKpis.length === 0) return showEmptyDash ? <EmptyDash /> : null;
  if (forceKpi === 'freeChoice' && uniqKpis.length === 1) {
    return <div className="kpi-cell"><KPIDisplay kpi={uniqKpis[0] as Record<string, unknown>} /></div>;
  }

  let kpiData: Record<string, unknown> | undefined;
  switch (type) {
    case 'allTime': kpiData = kpi; break;
    case 'recent': kpiData = recentKpi; break;
    case 'thisCampaign': kpiData = campaignKpi; break;
    case 'byFilteredCampaigns': kpiData = kpiByFilteredCampaigns; break;
    default: return showEmptyDash ? <EmptyDash /> : null;
  }

  if (isEmpty(kpiData)) return showEmptyDash ? <EmptyDash /> : null;

  return (
    <div className="kpi-wrapper">
      <div className="kpi-tab-row">
        {!isEmpty(kpi) && (
          <button
            type="button"
            className={`kpi-tab${type === 'allTime' ? ' kpi-tab--active' : ''}`}
            disabled={forceKpi !== 'freeChoice' && forceKpi !== 'allTime'}
            onClick={() => setType('allTime')}
          >All time</button>
        )}
        {!isEmpty(recentKpi) && (
          <button
            type="button"
            className={`kpi-tab${type === 'recent' ? ' kpi-tab--active' : ''}`}
            disabled={forceKpi !== 'freeChoice' && forceKpi !== 'recent'}
            onClick={() => setType('recent')}
          >Recent</button>
        )}
        {!isEmpty(campaignKpi) && (
          <button
            type="button"
            className={`kpi-tab${type === 'thisCampaign' ? ' kpi-tab--active' : ''}`}
            disabled={forceKpi !== 'freeChoice' && forceKpi !== 'thisCampaign'}
            onClick={() => setType('thisCampaign')}
          >This camp.</button>
        )}
        {!isEmpty(kpiByFilteredCampaigns) && (
          <button
            type="button"
            className={`kpi-tab${type === 'byFilteredCampaigns' ? ' kpi-tab--active' : ''}`}
            disabled={forceKpi !== 'freeChoice' && forceKpi !== 'byFilteredCampaigns'}
            onClick={() => setType('byFilteredCampaigns')}
          >Filtered</button>
        )}
      </div>
      {kpiData && <div className="kpi-cell"><KPIDisplay kpi={kpiData} /></div>}
    </div>
  );
}
